<script>
    import { apiUrl } from "@/utils";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import TheSvgIcon from "@main/packages/TheSvgIcon";
    import ProductCardButtons from "@/components/ProductCardButtons";
    import ProductColorsList from "@/components/ProductColorsList";
    import ProductPrice from "@mk/components/ProductPrice";

    export default {
        name: "ProductCard",
        components: {
            TheSvgIcon,
            ProductPrice,
            ProductCardButtons,
            ProductColorsList,
        },
        props: {
            product: Object,
            position: Number,
            isSmall: Boolean,
            inCartList: Array,
        },
        data() {
            let isPreOrder = false;
            let currentOffers = this.product.offers
                .filter((offer) => offer.available)
                .sort((a, b) => a.sort - b.sort);

            if (currentOffers.length === 0) {
                // проверка на предзаказ
                currentOffers = this.product.offers.filter((offer) => !offer.available);
                isPreOrder = true;
            }

            const previewImage =
                currentOffers[0].previewImage?.medium || this.product.previewImage?.medium;

            return {
                currentOffers,
                offer: currentOffers[0],
                offerId: currentOffers[0].id,
                previewImage,
                price: currentOffers[0].price,
                discountPrice: currentOffers[0].discountPrice,
                discountPercent: currentOffers[0].discountPercent,
                inCart: this.product.inCart || false,
                isPreOrder,
                previewImageFromOffer: "",
                hoverImageFromOffer: currentOffers[0].lifestyleImage?.medium,
            };
        },
        watch: {
            inCartList() {
                this.checkProductInCart();
            },
            offerId() {
                this.checkProductInCart();
            },
        },
        computed: {
            productPath() {
                return {
                    name: "Product",
                    params: { slug: this.product.slug, offer: this.offerId },
                };
            },
            nameProduct() {
                return this.product.shortName || this.product.name;
            },
            sizeButton() {
                return this.isSmall ? "sm" : "custom";
            },
            imageSrc() {
                return this.previewImageFromOffer ? apiUrl(this.previewImageFromOffer) : "";
            },
            hoverImageSrc() {
                return this.hoverImageFromOffer
                    ? this.hoverImageFromOffer
                    : this.product.additionalPreviewImage?.medium;
            },
        },
        mounted() {
            this.checkProductInCart();
        },
        methods: {
            apiUrl,
            checkProductInCart() {
                this.inCartList
                    ? (this.inCart = this.inCartList.includes(this.offerId.toString()))
                    : (this.inCart = false);
            },
            selectColor(color) {
                let { id, previewImage, price, discountPrice, available, lifestyleImage } =
                    this.currentOffers.filter((offer) => offer.color.id === color.id)[0];
                this.offerId = id;
                this.previewImageFromOffer = previewImage.medium;
                this.hoverImageFromOffer = lifestyleImage?.medium;
                this.price = price;
                this.discountPrice = discountPrice;
                this.isPreOrder = !available;
                this.inCart = false;
            },
            sendDataLayerEvent() {
                sendAnalytics.send(sendAnalytics.events.productClick, {
                    name: this.product.name,
                    offerId: this.offerId,
                    price: this.discountPrice,
                    position: this.position,
                });
            },
        },
    };
</script>

<template>
    <div class="product-card" :class="{ 'product-card--sm': isSmall }">
        <div class="product-card__header">
            <div v-if="discountPercent" class="product-card__discount">
                {{ discountPercent }}%
            </div>

            <div class="product-card__img">
                <router-link
                    :to="productPath"
                    class="product-card__img-link js-lazy-load"
                    tabindex="-1"
                    @click.native.prevent="sendDataLayerEvent"
                >
                    <img
                        class="product-card__img-product"
                        :class="{ 'is-preorder': isPreOrder }"
                        :src="imageSrc"
                        :data-src="apiUrl(previewImage)"
                        alt=""
                    />
                    <img class="product-card__img-hover" :src="apiUrl(hoverImageSrc)" alt="" />

                    <div class="product-card__preloader">
                        <preloader :show="true" lg-size></preloader>
                    </div>
                </router-link>
            </div>

            <div v-if="offer.color && currentOffers.length > 1" class="product-card__colors">
                <product-colors-list
                    :offers="currentOffers"
                    :current-offer="offer"
                    @select-color="selectColor"
                ></product-colors-list>
            </div>
        </div>

        <div class="product-card__footer">
            <router-link
                :to="productPath"
                class="product-card__price"
                @click.native.prevent="sendDataLayerEvent"
            >
                <product-price
                    :is-pre-order="isPreOrder && !isSmall"
                    :price="discountPrice"
                    :old-price="price"
                    :discount-count="discountPercent"
                    hide-discount-label
                    is-card-size
                ></product-price>
            </router-link>

            <router-link
                :to="productPath"
                class="product-card__name"
                v-html="nameProduct"
                @click.native.prevent="sendDataLayerEvent"
            >
            </router-link>

            <product-card-buttons
                class="product-card__buttons"
                :offer-id="offerId"
                :product="product"
                :is-pre-order="isPreOrder"
                :in-cart="inCart"
                :discount-price="discountPrice"
                :size-button="sizeButton"
                preorder-text-mobile="Уведомить"
                variant-preorder-button="v5"
                @on-added-to-cart="inCart = true"
            >
                <template v-if="inCart" #iconBuy>
                    <the-svg-icon class="icon" name="in_cart" :size-w="24"></the-svg-icon>
                </template>
                <template #iconPreorder>
                    <the-svg-icon class="icon" name="preorder" :size-w="24"></the-svg-icon>
                </template>
            </product-card-buttons>
        </div>
    </div>
</template>

<style lang="scss">
    .product-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 10px;
        background-color: #fff;
        border-radius: 16px;
        transition: 0.3s ease-in-out all;
        &.swiper-slide {
            height: auto;
        }
        &:hover {
            box-shadow: 0 10px 20px rgba(#2e2e2e, 0.15);
        }
        @include bp($bp-desktop-sm) {
            padding: 22px;
            border-radius: 36px;
        }
    }

    .product-card__discount {
        position: absolute;
        z-index: 5;
        top: 0;
        left: 0;
        width: 40px;
        height: 40px;
        color: #fff;
        font-size: 12px;
        font-weight: 500;
        line-height: 40px;
        text-align: center;
        border-radius: 50%;
        background-color: var(--danger);
        pointer-events: none;
        user-select: none;
        @include bp($bp-desktop-sm) {
            top: 4px;
            left: 4px;
            width: 90px;
            height: 90px;
            font-size: 30px;
            line-height: 90px;
        }
    }

    .product-card__header {
        position: relative;
        margin-bottom: 17px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 42px;
        }
    }

    .product-card__colors {
        position: absolute;
        z-index: 3;
        bottom: -14px;
        left: -10px;
        height: 28px;
        padding: 4px 7px;
        background-color: #fff;
        border-radius: 0 16px 16px 0;
        @include bp($bp-desktop-sm) {
            bottom: -22px;
            left: -22px;
            height: 44px;
            padding: 6px 6px 6px 17px;
            border-radius: 0 22px 22px 0;
        }
    }

    .product-card__img {
        position: relative;
        z-index: 1;
        border-radius: 8px;
        transition: 0.3s ease-in-out background-color;
        &:hover {
            .product-card__img-hover {
                opacity: 1;
            }
            .product-card__img-product {
                opacity: 0;
            }
        }
        @include bp($bp-desktop-sm) {
            border-radius: 15px;
        }
    }

    .product-card__img-link {
        position: relative;
        display: block;
        padding-bottom: 100%;
        overflow: hidden;
        border-radius: 8px;
        img {
            @include center-absolute(0);
            z-index: 2;
            margin: auto;
            max-height: 100%;
            user-select: none;
            pointer-events: none;
        }
        @include bp($bp-desktop-sm) {
            border-radius: 15px;
        }
    }

    .product-card__preloader {
        opacity: 1;
    }

    .product-card__img-product,
    .product-card__img-hover {
        border-radius: 8px;
        @include bp($bp-desktop-sm) {
            border-radius: 15px;
        }
    }

    .product-card__img-product {
        z-index: 3;
        display: none;
        transition: opacity 0.3s ease-in;
        animation: fadeIn 0.3s ease-in-out;

        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: scale(0.9);
            }
            to {
                opacity: 1;
                transform: scale(1);
            }
        }

        &.is-preorder {
            opacity: 0.3 !important;
        }

        &[lazy="loaded"] {
            display: block;
            & ~ .product-card__preloader {
                opacity: 0;
            }
        }
    }

    .product-card__img-hover {
        z-index: 1;
        transition: opacity 0.3s ease-in;
        opacity: 0;
    }

    .product-card__footer {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    .product-card__name {
        flex-grow: 1;
        display: block;
        margin-bottom: 20px;
        color: #000;
        font-size: 12px;
        line-height: 18px;
        transition: 0.3s ease-in-out all;
        @include bp($bp-desktop-sm) {
            margin-bottom: 40px;
            font-size: 22px;
            line-height: 30px;
        }
    }

    .product-card__price {
        display: block;
        margin-bottom: 5px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 20px;
        }
    }

    .product-card__buttons {
        svg.icon {
            display: none;
            @include bp($bp-desktop-sm) {
                display: block;
                margin-left: 10px;
            }
        }
    }

    //
    // --mod
    //

    .product-card--sm {
        @include bp($bp-desktop-sm) {
            padding: 10px;
            border-radius: 16px;
            .product-card__control-button--preorder.base-button {
                font-size: 12px;
            }
        }

        .product-card__discount {
            @include bp($bp-desktop-sm) {
                width: 40px;
                height: 40px;
                font-size: 12px;
                line-height: 40px;
            }
        }

        .product-card__name {
            @include bp($bp-desktop-sm) {
                margin-bottom: 20px;
                font-size: 12px;
                line-height: 18px;
            }
        }

        .product-card__colors {
            @include bp($bp-desktop-sm) {
                height: 28px;
                margin-top: -14px;
                margin-bottom: 3px;
                margin-left: -10px;
                padding: 4px 7px;
                background-color: #fff;
                border-radius: 0 16px 16px 0;
            }
        }

        .product-card__price {
            @include bp($bp-desktop-sm) {
                margin-bottom: 5px;
                .product-price__number {
                    font-size: 16px;
                    line-height: 16px;
                }
                .product-price__number--old {
                    margin-bottom: 2px;
                    font-size: 12px;
                    line-height: 12px;
                }
                .product-price__wrap {
                    flex-direction: column-reverse;
                }
            }
        }
    }
</style>
